import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import {
  ArrowLeft, Trophy, Target, Clock, Zap, Medal, Crown, Loader2, Users
} from 'lucide-react';

const WeeklyChallenges = () => {
  const { childId } = useParams();
  const { api } = useAuth();
  const [challenges, setChallenges] = useState([]);
  const [child, setChild] = useState(null);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(null);

  useEffect(() => {
    fetchData();
  }, [childId]);

  const fetchData = async () => {
    try {
      const [challengesRes, childRes] = await Promise.all([
        api.get('/challenges'),
        api.get(`/children/${childId}`)
      ]);
      setChallenges(challengesRes.data);
      setChild(childRes.data);
      
      // Get progress for each challenge
      const progressPromises = challengesRes.data.map(c => 
        api.get(`/challenges/${c.id}/my-progress?child_id=${childId}`)
          .catch(() => ({ data: { joined: false } }))
      );
      const progressResults = await Promise.all(progressPromises);
      
      const enrichedChallenges = challengesRes.data.map((c, i) => ({
        ...c,
        progress: progressResults[i].data
      }));
      setChallenges(enrichedChallenges);
    } catch (error) {
      console.error('Failed to fetch challenges:', error);
      toast.error('Failed to load challenges');
    } finally {
      setLoading(false);
    }
  };

  const handleJoinChallenge = async (challengeId) => {
    setJoining(challengeId);
    try {
      await api.post(`/challenges/${challengeId}/join?child_id=${childId}`);
      toast.success('Joined challenge!');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to join challenge');
    } finally {
      setJoining(null);
    }
  };

  const getChallengeIcon = (type) => {
    switch (type) {
      case 'xp_race': return Zap;
      case 'activities_count': return Target;
      case 'perfect_scores': return Trophy;
      case 'time_spent': return Clock;
      default: return Trophy;
    }
  };

  const getChallengeColor = (type) => {
    switch (type) {
      case 'xp_race': return 'from-yellow-400 to-orange-500';
      case 'activities_count': return 'from-blue-400 to-purple-500';
      case 'perfect_scores': return 'from-green-400 to-emerald-500';
      case 'time_spent': return 'from-pink-400 to-rose-500';
      default: return 'from-sky-400 to-blue-500';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-sky-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 to-white theme-kids">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-purple-200 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to={`/learn/${childId}`}>
                <Button variant="ghost" size="icon">
                  <ArrowLeft className="w-5 h-5" />
                </Button>
              </Link>
              <div className="flex items-center gap-2">
                <Trophy className="w-6 h-6 text-yellow-500" />
                <h1 className="font-fredoka text-xl font-bold text-purple-800">Weekly Challenges</h1>
              </div>
            </div>
            <Link to={`/leaderboard/${childId}`}>
              <Button variant="outline" className="gap-2" data-testid="view-leaderboard-btn">
                <Crown className="w-4 h-4" /> Leaderboard
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Child Info */}
        {child && (
          <div className="text-center mb-8">
            <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-purple-400 to-pink-400 flex items-center justify-center text-white text-3xl font-bold mb-2">
              {child.name?.charAt(0).toUpperCase()}
            </div>
            <h2 className="font-fredoka text-2xl font-bold text-purple-800">{child.name}</h2>
            <p className="text-purple-600">Level {child.current_level || 1} • {child.total_xp || 0} XP</p>
          </div>
        )}

        {/* Challenges Grid */}
        {challenges.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <Trophy className="w-16 h-16 text-purple-300 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-purple-800 mb-2">No Active Challenges</h3>
              <p className="text-purple-600">Check back soon for new weekly challenges!</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-6">
            {challenges.map((challenge, index) => {
              const Icon = getChallengeIcon(challenge.challenge_type);
              const progressPercent = challenge.progress?.joined 
                ? Math.min(100, (challenge.progress.current_value / challenge.target_value) * 100)
                : 0;
              
              return (
                <motion.div
                  key={challenge.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card className="overflow-hidden">
                    <div className={`h-2 bg-gradient-to-r ${getChallengeColor(challenge.challenge_type)}`} />
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${getChallengeColor(challenge.challenge_type)} flex items-center justify-center`}>
                            <Icon className="w-6 h-6 text-white" />
                          </div>
                          <div>
                            <CardTitle className="text-lg">{challenge.title}</CardTitle>
                            <p className="text-sm text-slate-500">{challenge.description}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="flex items-center gap-1 text-yellow-500 font-bold">
                            <Medal className="w-4 h-4" />
                            {challenge.coins_reward} coins
                          </div>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        {/* Progress */}
                        {challenge.progress?.joined ? (
                          <div>
                            <div className="flex justify-between text-sm mb-2">
                              <span className="text-slate-600">Progress</span>
                              <span className="font-medium">
                                {challenge.progress.current_value} / {challenge.target_value}
                              </span>
                            </div>
                            <Progress value={progressPercent} className="h-3" />
                            {challenge.progress.completed && (
                              <div className="flex items-center gap-2 mt-2 text-green-600">
                                <Trophy className="w-4 h-4" />
                                <span className="font-medium">Challenge Completed!</span>
                              </div>
                            )}
                            {challenge.progress.rank && (
                              <div className="flex items-center gap-2 mt-2 text-purple-600">
                                <Crown className="w-4 h-4" />
                                <span>Current Rank: #{challenge.progress.rank}</span>
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="flex items-center justify-between">
                            <div className="text-sm text-slate-500">
                              Target: {challenge.target_value} {challenge.challenge_type === 'time_spent' ? 'seconds' : ''}
                            </div>
                            <Button
                              onClick={() => handleJoinChallenge(challenge.id)}
                              disabled={joining === challenge.id}
                              className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
                              data-testid={`join-challenge-${challenge.id}`}
                            >
                              {joining === challenge.id ? (
                                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                              ) : (
                                <Users className="w-4 h-4 mr-2" />
                              )}
                              Join Challenge
                            </Button>
                          </div>
                        )}

                        {/* Challenge Info */}
                        <div className="flex justify-between text-xs text-slate-400 pt-2 border-t">
                          <span>Ends: {challenge.end_date}</span>
                          {challenge.subject && <span>Subject: {challenge.subject}</span>}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
};

export default WeeklyChallenges;
